<script setup>
import { reactive, computed, h, onMounted, onUnmounted } from 'vue'
import { useDataSourcesStore } from '@product/stores/dataSources'
import {
  useVueTable,
  getCoreRowModel,
  createColumnHelper,
  FlexRender
} from '@tanstack/vue-table'
import moment from 'moment'
import {
  getColumnConfig,
  getDefaultColumnWidth,
  DEFAULT_COLUMNS
} from '@/config/fieldDefaults'
import CellFilterButtons from '@/components/Selection/CellFilterButtons.vue'
import CellContextMenu from '@/components/Selection/CellContextMenu.vue'
import { useCellSelection } from '@/composables/useCellSelection'
import { useColumnStatsHover } from '@/composables/useColumnStatsHover'
import { makeSelectionItem, FILTER_OPERATORS, QUEUE_MODES, firstScalarLeaf } from '@/utils/selection'
import ColumnStatsPopover from './ColumnStatsPopover.vue'

const props = defineProps({
  results: {
    type: Array,
    default: () => []
  },
  logType: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  total: {
    type: Number,
    default: 0
  },
  currentPage: {
    type: Number,
    default: 0
  },
  pageSize: {
    type: Number,
    default: 50
  },
  moreResults: {
    type: Boolean,
    default: false
  },
  showPageSizeSelector: {
    type: Boolean,
    default: true
  },
  enabledFields: {
    type: Array,
    default: null // null means show all columns (fallback to config)
  },
  fieldMappings: {
    type: Object,
    default: () => ({})
  },
  sort: {
    type: Array,
    default: () => []
  },
  fieldTypes: {
    type: Object,
    default: () => ({})
  },
  baseQuery: {
    type: String,
    default: '*'
  },
  // Stats provider for the column header popover: (field) => { terms, cardinality }.
  // Null leaves headers inert, so read-only consumers get a plain table.
  columnStats: {
    type: Function,
    default: null
  },
  error: {
    type: String,
    default: ''
  },
  activeFilters: {
    type: Array,
    default: () => []
  },
  // Opt-in cell interactions (Kibana-style). Off by default so read-only
  // consumers keep a plain table.
  selectable: {
    type: Boolean,
    default: false
  },
  cellFilters: {
    type: Boolean,
    default: false
  },
  contextMenu: {
    type: Boolean,
    default: false
  },
  // Optional shared selection instance from a parent that wants to read the
  // selection state. Falls back to a private instance.
  selection: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['next-page', 'prev-page', 'apply-filter', 'sort-change', 'add-filter', 'update:page-size', 'navigate-aggregations', 'remove-filter', 'clear-filters'])

const pageSizeOptions = [50, 100, 250, 500, 750, 1000]

const dataSourcesStore = useDataSourcesStore()

const sel = props.selection || useCellSelection()

// Ctrl/Cmd+C copies the selected cells as a spreadsheet TSV grid. Only fires
// when this table has a selection and focus isn't in a field / no real text is
// selected, so native copy still works everywhere else.
function onCopyKey(e) {
  if (e.key !== 'c' || !(e.ctrlKey || e.metaKey)) return
  if (!sel.hasSelection.value) return
  const el = document.activeElement
  if (el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable) return
  if (String(window.getSelection?.() || '').length > 0) return
  e.preventDefault()
  sel.copySelectionGrid()
}
onMounted(() => document.addEventListener('keydown', onCopyKey))
onUnmounted(() => document.removeEventListener('keydown', onCopyKey))

// Column stats popover state
const {
  show: showStatsPopover,
  field: statsPopoverField,
  anchor: statsPopoverAnchor,
  onHeaderEnter: statsHeaderEnter,
  onHeaderLeave: handleHeaderMouseLeave,
  close: closeStatsPopover
} = useColumnStatsHover({ enabled: () => !!props.columnStats })

// Every cell except the raw-JSON dump is selectable (including empty cells).
function cellSelectable(cell) {
  return !!resolveCell(cell)
}

// Resolve a cell to a filter descriptor { field, rawValue, formattedValue, mode, empty }.
// Empty/missing cells filter on field existence; scalars filter as field:value;
// object/array cells filter value-only on their first scalar leaf.
function resolveCell(cell) {
  const colId = cell.column.id
  if (colId === '_raw') return null
  const raw = cell.getValue()
  if (raw === null || raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
    return { field: colId, rawValue: '', formattedValue: '(empty)', mode: QUEUE_MODES.FIELD_VALUE, empty: true }
  }
  if (typeof raw !== 'object') {
    return { field: colId, rawValue: raw, formattedValue: formatCellValue(raw, colId), mode: QUEUE_MODES.FIELD_VALUE }
  }
  const leaf = firstScalarLeaf(raw)
  if (leaf === undefined) return null
  return { field: colId, rawValue: leaf, formattedValue: formatCellValue(raw, colId), mode: QUEUE_MODES.VALUE_ONLY }
}

function cellDescriptor(row, cell, rowIndex, colIndex) {
  const r = resolveCell(cell)
  if (!r) return null
  return { rowId: row.id, colId: cell.column.id, rowIndex, colIndex, ...r }
}

// Enumerate + resolve the rectangle between two cells (host owns ordering).
function rectangleCells(anchor, target) {
  const rows = table.getRowModel().rows
  const r0 = Math.min(anchor.rowIndex, target.rowIndex)
  const r1 = Math.max(anchor.rowIndex, target.rowIndex)
  const c0 = Math.min(anchor.colIndex, target.colIndex)
  const c1 = Math.max(anchor.colIndex, target.colIndex)
  const cells = []
  for (let r = r0; r <= r1; r++) {
    const row = rows[r]
    if (!row) continue
    const visCells = row.getVisibleCells()
    for (let c = c0; c <= c1; c++) {
      const cell = visCells[c]
      if (!cell) continue
      const desc = cellDescriptor(row, cell, r, c)
      if (desc) cells.push(desc)
    }
  }
  return cells
}

function onCellMouseDown(event, row, cell, rowIndex, colIndex) {
  if (!props.selectable) return
  if (event.button !== 0) return  // right-click handled by onCellContextMenu
  if (event.target.closest?.('a, button, input')) return
  const desc = cellDescriptor(row, cell, rowIndex, colIndex)
  if (!desc) return
  if (event.shiftKey && sel.anchor.value) {
    event.preventDefault()
    sel.setRange(rectangleCells(sel.anchor.value, desc))
  } else if (event.ctrlKey || event.metaKey) {
    event.preventDefault()
    sel.toggle(desc)
  } else {
    sel.setSingleOrDeselect(desc)
  }
}

// Hover +/- : apply a single-cell filter immediately.
function onCellFilter(cell, negate) {
  const r = resolveCell(cell)
  if (!r) return
  const item = makeSelectionItem(r.field, r.rawValue, r.formattedValue, r.mode, r.empty)
  emit('apply-filter', [item], negate ? FILTER_OPERATORS.NOT : FILTER_OPERATORS.ALL)
}

// Right-click context menu.
const menu = reactive({ show: false, x: 0, y: 0, items: [] })

function onCellContextMenu(event, row, cell, rowIndex, colIndex) {
  if (!props.contextMenu) return
  event.preventDefault()
  const colId = cell.column.id
  if (!sel.isSelected(row.id, colId)) {
    const desc = cellDescriptor(row, cell, rowIndex, colIndex)
    if (!desc) return
    sel.setSingle(desc)
  }
  const items = sel.selectedItems.value
  if (!items.length) return
  menu.items = items
  menu.x = event.clientX
  menu.y = event.clientY
  menu.show = true
}

function closeMenu() {
  menu.show = false
}

function menuFilterFor() {
  emit('apply-filter', menu.items, FILTER_OPERATORS.ALL)
  sel.clear()
}

function menuFilterOut() {
  emit('apply-filter', menu.items, FILTER_OPERATORS.NOT)
  sel.clear()
}

function menuFilterForAny() {
  const anyItems = menu.items.map(i => ({ ...i, mode: QUEUE_MODES.VALUE_ONLY }))
  emit('apply-filter', anyItems, FILTER_OPERATORS.ANYOF)
  sel.clear()
}

function menuBulk(operator) {
  emit('apply-filter', menu.items, operator)
  sel.clear()
}

// Get value from object by key
function getValue(obj, key) {
  if (!obj || !key) return null
  // Try flat lookup first (covers both flat keys and pre-flattened dot keys)
  if (key in obj) return obj[key]
  // Traverse dot-notation for nested objects (e.g. "id.orig_h" → obj.id.orig_h)
  const parts = key.split('.')
  let val = obj
  for (const part of parts) {
    if (val == null || typeof val !== 'object') return null
    val = val[part]
  }
  return val ?? null
}

// Format timestamp for display
function formatTimestamp(value) {
  if (!value) return '-'

  // Handle unix timestamps (milliseconds)
  if (typeof value === 'number') {
    return moment(value).format('MMM DD, YYYY HH:mm:ss')
  }

  // Handle numeric strings (epoch milliseconds sent as string)
  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    return moment(Number(value)).format('MMM DD, YYYY HH:mm:ss')
  }

  // Handle ISO strings
  if (typeof value === 'string') {
    const m = moment(value)
    return m.isValid() ? m.format('MMM DD, YYYY HH:mm:ss') : value
  }

  return String(value)
}

// Format bytes for display
function formatBytes(bytes) {
  if (bytes === null || bytes === undefined || bytes === '-') return '-'

  const num = Number(bytes)
  if (isNaN(num)) return bytes

  if (num === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(num) / Math.log(1024))
  const size = (num / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)

  return `${size} ${units[Math.min(i, units.length - 1)]}`
}

// Format duration for display
function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return '-'

  const num = Number(seconds)
  if (isNaN(num)) return seconds

  if (num < 1) return `${(num * 1000).toFixed(0)}ms`
  if (num < 60) return `${num.toFixed(2)}s`
  if (num < 3600) return `${Math.floor(num / 60)}m ${Math.floor(num % 60)}s`

  return `${Math.floor(num / 3600)}h ${Math.floor((num % 3600) / 60)}m`
}

// Format array values
function formatArray(value) {
  if (!value) return '-'
  if (Array.isArray(value)) {
    return value.join(', ') || '-'
  }
  return String(value)
}

// Render 2-letter ISO country code as SVG flag + text
function formatCountryCode(code) {
  if (typeof code !== 'string' || code.length !== 2) return String(code)
  const upper = code.toUpperCase()
  const lower = code.toLowerCase()
  try {
    const src = require('@/assets/flags/4x3/' + lower + '.svg')
    return h('span', { style: 'display:inline-flex;align-items:center;gap:4px' }, [
      h('img', { src, height: 13, style: 'border-radius:3px' }),
      upper
    ])
  } catch {
    return upper
  }
}

// Format cell value based on field type
function formatCellValue(value, key) {
  if (value === null || value === undefined) return '-'

  // Annotated country_code fields — plain text for queue/filter display
  const annotation = dataSourcesStore.annotatedFields.get(key)
  if (annotation && annotation.role === 'country_code' && typeof value === 'string' && value.length === 2) {
    return value.toUpperCase()
  }

  // Timestamp fields
  if (key === '@timestamp' || key.includes('timestamp') || key.includes('valid_before') || key.includes('valid_after')) {
    return formatTimestamp(value)
  }

  // Byte fields
  if (key.includes('bytes') || key === 'total_bytes') {
    return formatBytes(value)
  }

  // Duration field
  if (key === 'duration') {
    return formatDuration(value)
  }

  // Array fields
  if (Array.isArray(value)) {
    return formatArray(value)
  }

  // Boolean fields
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  // Object fields (stringify)
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

// Format entire row as JSON (for fallback/raw display)
function formatRawDocument(row) {
  return JSON.stringify(row)
}

// Build column helper
const columnHelper = createColumnHelper()

// Build tanstack table columns from column definitions
function buildTableColumns(columnDefs) {
  return columnDefs.map(col => {
    if (col.key === '_raw') {
      return columnHelper.accessor(row => row, {
        id: '_raw',
        header: col.label,
        cell: info => formatRawDocument(info.getValue()),
        size: col.width || undefined,
        minSize: 200,
      })
    }

    return columnHelper.accessor(row => getValue(row, col.key), {
      id: col.key,
      header: col.label,
      cell: info => {
        const value = info.getValue()
        const annotation = dataSourcesStore.annotatedFields.get(col.key)
        if (annotation && annotation.role === 'country_code' && typeof value === 'string' && value.length === 2) {
          return formatCountryCode(value)
        }
        return formatCellValue(value, col.key)
      },
      size: col.width || undefined,
      minSize: col.key === '@timestamp' ? 180 : 60,
    })
  })
}

// Generate table columns based on log type, API field mappings, and enabled fields
// enabledFields is already ordered by the parent component via useFieldSettings
const tableColumns = computed(() => {
  const logType = props.logType
  const allFields = props.fieldMappings[logType] || []

  // If no API fields available, show default columns (ts + raw document)
  if (allFields.length === 0) {
    return buildTableColumns(DEFAULT_COLUMNS)
  }

  // Use enabledFields directly - already ordered and filtered by parent
  let fieldsToShow = props.enabledFields
  if (!fieldsToShow || fieldsToShow.length === 0) {
    fieldsToShow = allFields
  }

  // Build column definitions - use field key as label, config only for width
  const columnDefs = fieldsToShow.map(fieldKey => {
    const config = getColumnConfig(logType, fieldKey)

    return {
      key: fieldKey,
      label: fieldKey,
      width: config?.width || getDefaultColumnWidth(fieldKey)
    }
  })

  return buildTableColumns(columnDefs)
})

// Create table instance
const table = useVueTable({
  get data() {
    return props.results
  },
  get columns() {
    return tableColumns.value
  },
  getCoreRowModel: getCoreRowModel(),
})

// Sort helper functions
function getSortState(fieldName) {
  const index = props.sort.findIndex(s => s.field === fieldName)
  if (index === -1) return null
  return {
    direction: props.sort[index].direction,
    priority: index + 1
  }
}

function getFieldTypeInfo(fieldName) {
  return props.fieldTypes[fieldName] || { type: 'unknown', hasKeyword: false }
}

function handleHeaderClick(event, fieldName) {
  if (fieldName === '_raw') return
  const fieldInfo = getFieldTypeInfo(fieldName)
  emit('sort-change', fieldName, fieldInfo)
}

// Column stats popover handlers. The raw-JSON dump has no aggregatable field.
function handleHeaderMouseEnter(event, fieldName) {
  statsHeaderEnter(event, fieldName === '_raw' ? null : fieldName)
}

function handlePopoverAddFilter(filterData) {
  emit('add-filter', filterData)
  closeStatsPopover()
}

function handleNavigateAggregations(field) {
  emit('navigate-aggregations', field)
}
</script>

<template>
  <div class="zeek-logs-table">
    <!-- Loading overlay -->
    <div v-if="loading && results.length > 0 && !error" class="loading-overlay">
      <div class="spinner"></div>
    </div>

    <!-- Error state -->
    <div v-if="error" class="state-overlay">
      <div class="state-box error-box">
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="error-icon" />
        <span class="state-message">{{ error }}</span>
      </div>
    </div>

    <!-- Loading state when no results yet -->
    <div v-else-if="loading && results.length === 0" class="state-overlay">
      <div class="state-box">
        <div class="spinner"></div>
        <span class="state-message">Loading results...</span>
      </div>
    </div>

    <!-- Empty state when query returned no results -->
    <div v-else-if="results.length === 0 && !loading" class="state-overlay">
      <div class="state-box">
        <span class="state-message">No results found</span>
        <span class="state-hint">Try adjusting your query or time range</span>
      </div>
    </div>

    <!-- Table -->
    <template v-else>
      <div class="table-container">
        <div class="table-scroll">
          <table>
            <thead>
              <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                <th
                  v-for="header in headerGroup.headers"
                  :key="header.id"
                  :style="{ width: header.getSize() ? `${header.getSize()}px` : 'auto' }"
                  :class="{ 'sortable-header': header.column.id !== '_raw' }"
                  @click="handleHeaderClick($event, header.column.id)"
                  @mouseenter="handleHeaderMouseEnter($event, header.column.id)"
                  @mouseleave="handleHeaderMouseLeave($event)"
                >
                  <div class="header-content">
                    <span class="header-label">
                      <FlexRender
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
                      />
                    </span>
                    <span class="sort-indicator" :class="{ 'sort-active': getSortState(header.column.id) }">
                      {{ getSortState(header.column.id)?.direction === 'asc' ? '▲' : '▼' }}
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody :class="{ 'selectable': selectable }">
              <tr v-for="(row, rowIndex) in table.getRowModel().rows" :key="row.id">
                <td
                  v-for="(cell, colIndex) in row.getVisibleCells()"
                  :key="cell.id"
                  :class="{
                    'cell-timestamp': cell.column.id === '@timestamp',
                    'cell-selected': selectable && sel.isSelected(row.id, cell.column.id),
                    'has-cell-filters': cellFilters && cellSelectable(cell)
                  }"
                  @mousedown="onCellMouseDown($event, row, cell, rowIndex, colIndex)"
                  @contextmenu="onCellContextMenu($event, row, cell, rowIndex, colIndex)"
                >
                  <div class="cell-content">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                  <CellFilterButtons
                    v-if="cellFilters && cellSelectable(cell)"
                    @filter-for="onCellFilter(cell, false)"
                    @filter-out="onCellFilter(cell, true)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Table Footer -->
      <div class="table-footer">
        <!-- Selection count (left side) -->
        <div class="footer-left">
          <div v-if="selectable && sel.hasSelection.value" class="selection-count">
            <span class="sel-count">{{ sel.count.value }}</span>
            <span class="sel-label">{{ sel.count.value === 1 ? 'cell' : 'cells' }} selected</span>
            <button class="sel-clear" @click="sel.clear()" title="Clear selection">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </button>
          </div>
        </div>

        <!-- Pagination (center) -->
        <div class="pagination-controls">
          <button
            @click="$emit('prev-page')"
            :disabled="loading || currentPage === 0"
            class="btn-pagination"
          >
            <font-awesome-icon :icon="['fas', 'angle-left']" />
          </button>
          <span class="result-info">
            {{ currentPage * pageSize + 1 }}-{{ Math.min((currentPage + 1) * pageSize, total) }} of {{ total.toLocaleString() }}
          </span>
          <button
            @click="$emit('next-page')"
            :disabled="loading || !moreResults"
            class="btn-pagination"
          >
            <font-awesome-icon :icon="['fas', 'angle-right']" />
          </button>
        </div>

        <!-- Right side (active filters + page size) -->
        <div class="footer-right">
          <div v-if="activeFilters.length > 0" class="active-filters">
            <div
              v-for="filter in activeFilters"
              :key="filter.id"
              class="filter-tag"
            >
              <span class="filter-field">{{ filter.field }}:</span>
              <span class="filter-value">{{ filter.displayValue }}</span>
              <button class="filter-remove" @click="emit('remove-filter', filter.id)">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
            <button v-if="activeFilters.length > 1" class="clear-all-btn" @click="emit('clear-filters')">
              Clear all
            </button>
          </div>
          <div v-if="showPageSizeSelector" class="dropup">
            <button
              class="btn-page-size dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {{ pageSize }} rows
            </button>
            <ul class="dropdown-menu dropdown-menu-end page-size-menu">
              <li v-for="size in pageSizeOptions" :key="size">
                <button
                  class="dropdown-item"
                  :class="{ active: size === pageSize }"
                  @click="emit('update:page-size', size)"
                >
                  {{ size }}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <!-- Cell Context Menu -->
    <CellContextMenu
      :show="menu.show"
      :x="menu.x"
      :y="menu.y"
      :items="menu.items"
      :count="sel.count.value"
      @filter-for="menuFilterFor"
      @filter-out="menuFilterOut"
      @filter-for-any="menuFilterForAny"
      @apply-bulk="menuBulk"
      @open-selector="sel.openInSelectorSearch(menu.items)"
      @open-event="sel.openInEventSearch(menu.items)"
      @copy="sel.copyItems(menu.items)"
      @close="closeMenu"
    />

    <!-- Column Stats Popover -->
    <ColumnStatsPopover
      v-if="columnStats && statsPopoverField"
      :show="showStatsPopover"
      :field="statsPopoverField"
      :provider="columnStats"
      :anchor-el="statsPopoverAnchor"
      expandable
      @add-filter="handlePopoverAddFilter"
      @close="closeStatsPopover"
      @navigate-aggregations="handleNavigateAggregations"
    />
  </div>
</template>

<style scoped>
.zeek-logs-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  overflow: hidden;
  position: relative;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

/* State Overlay (Loading/Empty) */
.state-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 26, 34, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 3rem;
  background: var(--surface-page);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.state-message {
  color: var(--text-primary);
  font-size: 0.95rem;
  text-align: center;
}

.state-hint {
  color: var(--text-faint);
  font-size: 0.8rem;
  text-align: center;
}

/* Error State */
.error-box {
  border-color: rgba(158, 74, 74, 0.4);
}

.error-icon {
  font-size: 2rem;
  color: var(--color-error);
  opacity: 0.8;
}

/* Spinner */
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--text-disabled);
  border-top: 3px solid var(--accent);
  border-radius: var(--radius-pill);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Table Container */
.table-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
}

/* Scrollbar styling */
.table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4A5661 0%, #3E4A56 100%);
  border-radius: 10px;
  border: 2px solid rgba(0, 0, 0, 0.2);
}

.table-scroll::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5A6671 0%, #4E5A66 100%);
}

.table-scroll::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.2);
}

/* Table Styling */
table {
  min-width: 100%;
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
}

thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

thead th {
  background-color: var(--surface-card);
  color: var(--text-muted);
  text-align: left;
  padding: 1rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid var(--border-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Sortable Headers */
thead th.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

thead th.sortable-header:hover {
  background-color: var(--surface-hover);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
}

.header-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sort-indicator {
  color: var(--color-warning);
  font-size: 0.65rem;
  flex-shrink: 0;
  margin-left: auto;
  opacity: 0;
  min-width: 0.65rem;
}

.sort-indicator.sort-active {
  opacity: 1;
}

tbody td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: var(--text-primary);
  font-size: 0.85rem;
  vertical-align: top;
  position: relative;
}

tbody tr {
  transition: background-color 0.15s ease;
}

tbody tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.015);
}

tbody tr:nth-child(odd) {
  background-color: transparent;
}

tbody tr:hover {
  background-color: var(--accent-soft) !important;
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Timestamp cell */
.cell-timestamp {
  color: var(--text-muted);
  font-family: 'Cutive Mono', monospace;
  font-weight: 500;
}

/* Selectable mode - disable text selection so drag/shift-click select cleanly */
tbody.selectable {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

tbody.selectable td {
  cursor: pointer;
}

/* Cell Selection State */
.cell-selected {
  background: rgba(115, 87, 210, 0.12) !important;
}

/* Reserve room on the right so the hover +/- buttons never cover content */
tbody td.has-cell-filters {
  padding-right: 2.75rem;
}

/* Table Footer */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
  background: var(--surface-card);
}

.footer-left,
.footer-right {
  flex: 1;
  min-width: 0;
}

.footer-left {
  display: flex;
  justify-content: flex-start;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

.btn-page-size {
  padding: 0.4rem 0.6rem;
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.8rem;
}

.btn-page-size:hover {
  background: var(--surface-overlay-2);
  color: var(--text-heading);
}

.page-size-menu {
  background-color: var(--surface-raised);
  border: 1px solid var(--border);
  min-width: 5rem;
}

.page-size-menu .dropdown-item {
  color: var(--text-muted);
  font-size: 0.8rem;
  padding: 0.35rem 0.75rem;
}

.page-size-menu .dropdown-item:hover {
  background: var(--surface-overlay-2);
  color: var(--text-heading);
}

.page-size-menu .dropdown-item.active {
  background: rgba(115, 87, 210, 0.2);
  color: var(--accent-text);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.pagination-controls .btn-pagination {
  padding: 0.4rem 0.6rem;
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-controls .btn-pagination:hover:not(:disabled) {
  background: var(--surface-overlay-2);
  color: var(--text-heading);
}

.pagination-controls .btn-pagination:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.result-info {
  color: var(--text-faint);
  font-size: 0.8rem;
}

/* Selection count chip */
.selection-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-sm);
  user-select: none;
}

.sel-count {
  background: var(--accent);
  color: white;
  padding: 0.1rem 0.4rem;
  border-radius: var(--radius-xs);
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 1.25rem;
  text-align: center;
}

.sel-label {
  color: var(--text-primary);
  font-size: 0.75rem;
}

.sel-clear {
  background: none;
  border: none;
  color: var(--text-faint);
  padding: 0.15rem;
  cursor: pointer;
  border-radius: var(--radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
  margin-left: 0.25rem;
}

.sel-clear:hover {
  background: rgba(239, 68, 68, 0.2);
  color: var(--color-error);
}

/* Active Filters */
.active-filters {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-tag {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-xs);
  font-size: 0.625rem;
}

.filter-field {
  color: var(--text-muted);
}

.filter-value {
  color: var(--text-primary);
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-remove {
  background: none;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  font-size: 0.5rem;
  border-radius: var(--radius-xs);
  transition: var(--transition-fast);
}

.filter-remove:hover {
  color: var(--color-error);
  background: rgba(239, 68, 68, 0.2);
}

.clear-all-btn {
  background: none;
  border: none;
  color: var(--text-faint);
  font-size: 0.625rem;
  cursor: pointer;
  padding: 0.125rem 0.25rem;
  transition: color 0.15s ease;
}

.clear-all-btn:hover {
  color: var(--color-error);
}
</style>
